import { AltOffering } from './entities';
import {
  sortByHighestMinimumInvestment,
  sortByHighestYield,
  sortByLowestMinimumInvestment,
  sortByLowestYield,
  sortByNewest,
  sortByRecommended,
} from './utils';

export type FilterOptionsSortBy =
  | 'Recommended'
  | 'Newest'
  | 'Highest Yield'
  | 'Lowest Yield'
  | 'Highest Minimum Investment'
  | 'Lowest Minimum Investment';

export interface FilterOptions {
  investorType?: string;
  propertyTypes?: string[];
  categoryTypes?: string[];
  targetReturn?: string[];
  sortBy?: FilterOptionsSortBy;
  minimumInvestment?: [number, number];
  rating?: string;
  textSearch?: string;
  featured?: boolean;
  searchType?: string;
  page?: number;
  verticalTypes?: string;
}

export class AltOfferingsStore {
  private offerings?: AltOffering[];

  constructor() {
    this.offerings = undefined;
  }

  public filterOfferings = (offeringsToFilter: AltOffering[], values: FilterOptions): AltOffering[] => {
    const featuredFiltered =
      typeof values.featured === 'boolean'
        ? offeringsToFilter.filter(offering => offering.featured === values.featured)
        : offeringsToFilter;

    const minimumInvestmentFiltered = values.minimumInvestment
      ? featuredFiltered.filter(
          offering =>
            values.minimumInvestment &&
            values.minimumInvestment[0] <= offering.minInvestment &&
            values.minimumInvestment[1] >= offering.minInvestment,
        )
      : featuredFiltered;

    const nonAccredited = minimumInvestmentFiltered.filter(item => item.nonAccredited);
    const accredited = minimumInvestmentFiltered.filter(item => !item.nonAccredited);

    const filterInvestorType = () => {
      let result = minimumInvestmentFiltered;
      if (values.investorType === 'All') {
        return result;
      } else if (values.investorType === 'Accredited') {
        result = [...accredited];
      } else if (values.investorType === 'Non-Accredited') {
        result = [...nonAccredited];
      }
      return result;
    };

    const investorTypeFiltered = filterInvestorType();

    const propertyTypeFiltered =
      !values.propertyTypes || values.propertyTypes.length === 0
        ? investorTypeFiltered
        : investorTypeFiltered.filter(offering => {
            return values.propertyTypes && values.propertyTypes.includes(offering.propertyType);
          });

    const targetReturnFiltered =
      !values.targetReturn || values.targetReturn.length === 0
        ? propertyTypeFiltered
        : propertyTypeFiltered.filter(offering => {
            if (!offering.projectedReturnLow || !values.targetReturn) return false;
            if (values.targetReturn.includes('20%+')) {
              return offering.projectedReturnLow * 100 >= 20;
            }
            if (values.targetReturn.includes('15-20%')) {
              return offering.projectedReturnLow * 100 >= 15 && offering.projectedReturnLow * 100 <= 20;
            }
            if (values.targetReturn.includes('10-15%')) {
              return offering.projectedReturnLow * 100 >= 10 && offering.projectedReturnLow * 100 <= 15;
            }
            if (values.targetReturn.includes('5-10%')) {
              return offering.projectedReturnLow * 100 >= 5 && offering.projectedReturnLow * 100 <= 10;
            }
            if (values.targetReturn.includes('<5%')) {
              return offering.projectedReturnLow * 100 <= 5;
            }
            return false;
          });

    const categoryTypeFiltered =
      !values.categoryTypes || values.categoryTypes.length === 0
        ? targetReturnFiltered
        : targetReturnFiltered.filter(offering => {
            return values.categoryTypes && values.categoryTypes.includes(offering.type);
          });

    const searchFiltered = values.textSearch
      ? categoryTypeFiltered.filter(
          offering => values.textSearch && offering.name.toLowerCase().includes(values.textSearch.toLowerCase()),
        )
      : categoryTypeFiltered;

    const sortedRecommended = searchFiltered.slice().sort(sortByRecommended);
    const sortedNewest = searchFiltered.slice().sort(sortByNewest);
    const sortedHighestYield = searchFiltered.slice().sort(sortByHighestYield);
    const sortedLowestYield = searchFiltered.slice().sort(sortByLowestYield);
    const sortedHighestMinimumInvestment = searchFiltered.slice().sort(sortByHighestMinimumInvestment);
    const sortedLowestMinimumInvestment = searchFiltered.slice().sort(sortByLowestMinimumInvestment);

    const getRecommended = () => {
      const sorted = sortedRecommended;
      const filtered = sorted.filter((offering: AltOffering) => offering.priority);
      if (!filtered.length) return sorted;
      return filtered;
    };

    const result =
      values.sortBy === 'Recommended'
        ? getRecommended()
        : values.sortBy === 'Newest'
          ? sortedNewest
          : values.sortBy === 'Highest Yield'
            ? sortedHighestYield
            : values.sortBy === 'Lowest Yield'
              ? sortedLowestYield
              : values.sortBy === 'Highest Minimum Investment'
                ? sortedHighestMinimumInvestment
                : values.sortBy === 'Lowest Minimum Investment'
                  ? sortedLowestMinimumInvestment
                  : sortedRecommended;

    return result;
  };

  public getOfferings = (): AltOffering[] | undefined => {
    return this.offerings;
  };

  public getReviews = (): AltOffering[] | undefined => {
    return this.offerings;
  };

  public updateOfferings = (offerings: AltOffering[]): AltOffering[] => {
    this.offerings = offerings;
    return offerings;
  };
}
